import { useRecoilValue } from 'recoil';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import type { Image3D } from '~/domain/patient/image3d';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

/** The DICOM identities of the objects a workflow runs on; each absent when not loaded. */
export type WorkflowTargetIds = {
    primaryImage?: {
        seriesInstanceUID: string | undefined;
        studyInstanceUID: string | undefined;
    };
    secondaryImage?: {
        seriesInstanceUID: string | undefined;
        studyInstanceUID: string | undefined;
    };
    structureSet?: {
        sopInstanceUID: string | undefined;
    };
    dose?: {
        sopInstanceUID: string | undefined;
    };
};

function imageIds(image: Image3D): WorkflowTargetIds['primaryImage'] {
    return {
        seriesInstanceUID: image.seriesUid,
        studyInstanceUID: image.studyUid,
    };
}

/**
 * The `WorkflowTargetIds` a backend workflow runs on: the selected
 * primary image, structure set and dose, which every workflow form displays,
 * plus the image fused onto the primary in the primary row.
 * `undefined` when no primary image is selected.
 */
export function useWorkflowTargetIds(): WorkflowTargetIds | undefined {
    const primaryImage = useSelectedPrimaryImage();
    const { secondaryImage } = useActiveFusionTarget();
    const structureSet = useRecoilValue(currentStructureSetState);
    const dose = useCurrentDose();

    if (!primaryImage) {
        return undefined;
    }
    return {
        primaryImage: imageIds(primaryImage),
        secondaryImage: secondaryImage && imageIds(secondaryImage),
        structureSet: structureSet && { sopInstanceUID: structureSet.dicomUid },
        dose: dose && { sopInstanceUID: dose.sopInstanceUid },
    };
}
